import React, { Fragment } from "react";
import { Button, Chip, DialogContent, IconButton, TextField, Typography } from "@mui/material";
import { Delete, Upload } from "@mui/icons-material";
import { FileDragUpload } from "../../../../common/component/file-drag-upload/file-drag-upload";
import { BtmReturnInspectionProduct, BtmReturnInspectionStatus } from "../model/btm-return-inspection-product";
import { useIsDraggingFile } from "../../../../common/hooks/use-is-dragging-file";
import { FileToExchangeDto } from "../../../../helpers/file-to-exchange-dto";
import { IUploadedFileDto } from "../../../../common/model/file-exchange-dto";
import Box from "@mui/material/Box";
import { t } from "i18next";

interface IBtmFailedInspectionContentProps {
    data: BtmReturnInspectionProduct;
    status: BtmReturnInspectionStatus;
    commentTextValue: string;
    onCommentValueChange: (value: string) => void;
    filesList: IUploadedFileDto[];
    onFilesListChange: (files: IUploadedFileDto[]) => void;
}

export const BtmFailedInspectionContent = (props: IBtmFailedInspectionContentProps) => {
    const isDraggingFile = useIsDraggingFile();
    const handleFileUploaded = async (file: File) => {
        const fileExchangeDto = await FileToExchangeDto(file);
        props.onFilesListChange([...props.filesList, fileExchangeDto]);
    };

    const handleFileDelete = (index: number) => {
        props.onFilesListChange([...props.filesList].filter((_, i) => i !== index));
    };

    return <Fragment>
        <Box className={isDraggingFile ? "btm-failed-inspection-dialog--dragging" : ""} />
        <DialogContent>
            <div className={"btm-failed-inspection-dialog__details"}>
                <div className={"btm-failed-inspection-dialog__content-line"}>
                    <Typography color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.bulkInspection.columns.productName")}
                    </Typography>
                    <Typography>{props.data.productName}</Typography>
                </div>
                <div className={"btm-failed-inspection-dialog__content-line"}>
                    <Typography color={"textSecondary"}>
                        {t("components.blueBrandReturns.page.details.bulkInspection.columns.decision")}
                    </Typography>
                    <BtmInspectionStatus status={props.status} />
                </div>
            </div>
            <TextField multiline fullWidth minRows={4} maxRows={8} value={props.commentTextValue}
                       placeholder={t("components.blueBrandReturns.page.details.bulkInspection.columns.commentPlaceholder")}
                       onChange={e => props.onCommentValueChange(e.target.value)} />
            {props.filesList.length > 0 && <div className={"btm-failed-inspection-dialog__files"}>
                {props.filesList.map((file, index) =>
                    <div className={"btm-failed-inspection-dialog__file"} key={`file-${index}`}>
                        <img src={`data:image/jpeg;base64,${file.content}`}
                             className={"btm-failed-inspection-dialog__image"}
                             key={file.filename} />
                        <div data-action={"delete"}>
                            <IconButton color={"error"} onClick={() => handleFileDelete(index)}>
                                <Delete />
                            </IconButton>
                        </div>
                    </div>)}
            </div>}
            <FileDragUpload className={"btm-failed-inspection-dialog__file-upload"}
                            isDragging={isDraggingFile} disableOnClick={false}
                            onFileUploaded={handleFileUploaded} acceptedFiles={"image/*"}>
                {!isDraggingFile ?
                    <Button className={"btm-failed-inspection-dialog__upload"} color={"secondary"} variant={"outlined"} size={"large"} fullWidth>
                        <>{props.filesList.length === 0 ?
                            t("components.blueBrandReturns.page.details.bulkInspection.columns.uploadFile") :
                            t("components.blueBrandReturns.page.details.bulkInspection.columns.uploadAnotherFile")}</>
                        <Upload />
                    </Button> :
                    <Typography className={"btm-failed-inspection-dialog__dragging-label"}>
                        {t("components.blueBrandReturns.page.details.bulkInspection.columns.uploadFileDropText")}
                    </Typography>}
            </FileDragUpload>
        </DialogContent>
    </Fragment>;
};

const BtmInspectionStatus = ({ status }: { status: BtmReturnInspectionStatus }) => {
    switch (status) {
        case BtmReturnInspectionStatus.ConditionallyAccepted:
            return <Chip label={t("components.blueBrandReturns.inspectionStatus.ConditionallyAccepted")}
                         color={"warning"} variant={"outlined"} />;
        case BtmReturnInspectionStatus.Refused:
            return <Chip label={t("components.blueBrandReturns.inspectionStatus.Refused")}
                         color={"error"} variant={"outlined"} />;
        default:
            return <Chip label={t("components.blueBrandReturns.inspectionStatus.None")}
                         variant={"outlined"} />;
    }
};
